import { create } from 'zustand';
import { authApi, usersApi } from '../lib/apiClient';

function normalizeRole(role) {
  return role === 'vice_leader' ? 'vice-leader' : role;
}

function normalizeUser(user) {
  if (!user) return null;
  const displayName = user.displayName ?? user.name ?? user.display_name ?? '';
  const avatarIndex = user.avatarIndex ?? user.avatar ?? user.avatar_index ?? 0;
  const streakDays = user.streakDays ?? user.streak ?? user.streak_days ?? 0;

  return {
    ...user,
    displayName,
    name: displayName,
    avatarIndex,
    avatar: avatarIndex,
    role: normalizeRole(user.role),
    groupId: user.activeGroupId ?? user.active_group_id ?? user.groupId ?? user.group_id ?? null,
    streakDays,
    streak: streakDays,
    totalSeconds: user.totalSeconds ?? user.total_seconds ?? 0,
  };
}

const savedState = (() => {
  try {
    const state = JSON.parse(localStorage.getItem('auth-store') || '{}');
    return state;
  } catch {
    return {};
  }
})();

const savedUser = normalizeUser(savedState.user);

export const useAuthStore = create((set, get) => ({
  user: savedUser,
  role: savedUser?.role || null,
  token: savedState.token || null,
  groupId: savedUser?.groupId || null,
  groups: savedState.groups || [],
  isAuthenticated: !!savedState.token,
  isLoading: false,
  error: null,

  signIn: async (email, password) => {
    const normalizedEmail = (email || '').toLowerCase().trim();
    set({ isLoading: true, error: null });
    try {
      const res = await authApi.signIn(normalizedEmail, password);
      const user = normalizeUser(res.user);
      localStorage.setItem('auth-store', JSON.stringify({ token: res.token, user, groups: res.groups || [] }));
      set({
        user,
        role: user.role,
        token: res.token,
        groupId: user.groupId,
        groups: res.groups || [],
        isAuthenticated: true,
        isLoading: false,
      });
      return { success: true };
    } catch (err) {
      set({ error: err.message || 'Invalid credentials', isLoading: false });
      return { success: false, error: err.message || 'Invalid credentials' };
    }
  },

  signUp: async (name, email, password) => {
    const normalizedEmail = (email || '').toLowerCase().trim();
    set({ isLoading: true, error: null });
    try {
      const res = await authApi.signUp(name, normalizedEmail, password);
      const user = normalizeUser(res.user);
      localStorage.setItem('auth-store', JSON.stringify({ token: res.token, user, groups: res.groups || [] }));
      set({
        user,
        role: user.role,
        token: res.token,
        groupId: user.groupId,
        groups: res.groups || [],
        isAuthenticated: true,
        isLoading: false,
      });
      return { success: true, needsOtp: false };
    } catch (err) {
      set({ error: err.message || 'Signup failed', isLoading: false });
      return { success: false, error: err.message || 'Signup failed' };
    }
  },


  verifyOtp: async (email, code) => {
    const normalizedEmail = (email || '').toLowerCase().trim();
    set({ isLoading: true });
    try {
      await authApi.verifyOtp(normalizedEmail, code);
      set({ isLoading: false });
      return { success: true };
    } catch (err) {
      set({ isLoading: false });
      return { success: false };
    }
  },

  fetchUser: async () => {
    if (!get().token) return;
    set({ isLoading: true });
    try {
      const res = await usersApi.getMe();
      const normalizedUser = normalizeUser(res.user);
      get().updateState({
        user: normalizedUser,
        role: normalizedUser.role,
        groupId: normalizedUser.groupId,
        groups: res.groups || [],
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err) {
      get().signOut();
    }
  },

  updateUser: async (updates) => {
    try {
      const res = await usersApi.updateMe(updates);
      const user = normalizeUser({ ...get().user, ...updates, ...res.user });
      get().updateState({ user, role: user.role, groupId: user.groupId });
    } catch (err) {
      console.error('Failed to update profile', err);
    }
  },

  setGroup: (groupId) => {
    const state = get();
    const updatedUser = normalizeUser({ ...state.user, groupId });
    get().updateState({
      groupId,
      user: updatedUser,
    });
  },

  signOut: () => {
    localStorage.removeItem('auth-store');
    get().updateState({
      user: null,
      role: null,
      token: null,
      groupId: null,
      groups: [],
      isAuthenticated: false,
    });
  },

  clearError: () => set({ error: null }),
}));
